import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_TYPES = [
  "University / College course",
  "Secondary school / sixth form",
  "Professional development / upskilling",
  "Internship / apprenticeship",
  "Parent exploring options for child",
  "Not sure yet"
];
const ROLES = ["Student", "Working professional", "Parent or guardian"];
const COUNTRIES = ["India", "UK", "UAE", "Other"];
const TIME_HORIZONS = ["Within 3 months", "3-12 months", "More than 1 year"];
const BUDGET_LABELS = { 0: "Low", 1: "Medium", 2: "High" };

const WHY_NOW = [
  "Upcoming application deadline", "Changing careers / industries",
  "Exploring options out of curiosity", "Parent exploring for child",
  "Employer-mandated training", "Other"
];
const WHAT_MATTERS = [
  "Job outcomes", "Passion / subject interest", "Prestige / ranking",
  "Cost / financial aid", "Location convenience", "Flexibility (online/hybrid vs on-site)",
  "Credentials (degree vs certificate)"
];
const CONSTRAINTS = [
  "Must be within commuting distance", "Need scholarship / financial aid",
  "Need part-time / evening classes", "Must complete within \u226412 months",
  "Must be accredited / certified"
];
const LEARNING_STYLES = ["Self-paced", "Cohort-based", "Instructor-led", "Mixed / not sure"];

const MODALITIES = ["On-site only", "Hybrid", "Fully online", "Not sure"];
const STRENGTHS = [
  "Problem solving", "Communication", "Creativity",
  "Working with data", "Leading / organising", "Attention to detail"
];

const STEP_INFO = [
  { title: "About you", sub: "Tell us your situation so we can tailor what comes next." },
  { title: "Motivations and priorities", sub: "Understanding what drives you helps us identify what truly matters." },
  { title: "Interests and assumptions", sub: "We will surface assumptions worth checking and match relevant topics." },
  { title: "Quick concept check", sub: "A brief readiness signal \u2014 no judgement, just useful data." }
];

const stepVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.18 } }
};

// Persist helper
const STORAGE_KEY = "synaptick_sprint_state";
function loadState() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
}
function saveState(state) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export default function ClaritySprint() {
  const navigate = useNavigate();
  const saved = loadState();

  const [step, setStep] = useState(saved.step || 0);
  const [loading, setLoading] = useState(false);
  const [startTime] = useState(Date.now());

  // Step 1
  const [pathType, setPathType] = useState(saved.pathType || sessionStorage.getItem("prefill_path_type") || "");
  const [role, setRole] = useState(saved.role || "");
  const [country, setCountry] = useState(saved.country || sessionStorage.getItem("prefill_location") || "India");
  const [remoteOnly, setRemoteOnly] = useState(saved.remoteOnly || false);
  const [hybridOk, setHybridOk] = useState(saved.hybridOk || false);
  const [budgetIdx, setBudgetIdx] = useState(saved.budgetIdx ?? 1);
  const [budgetRange, setBudgetRange] = useState(saved.budgetRange || "");
  const [timeHorizon, setTimeHorizon] = useState(saved.timeHorizon || "");
  const [clarityBefore, setClarityBefore] = useState(saved.clarityBefore || 3);

  // Step 2
  const [whyNow, setWhyNow] = useState(saved.whyNow || []);
  const [whyNowOther, setWhyNowOther] = useState(saved.whyNowOther || "");
  const [priorities, setPriorities] = useState(saved.priorities || []);
  const [constraints, setConstraints] = useState(saved.constraints || []);
  const [learningStyle, setLearningStyle] = useState(saved.learningStyle || "");

  // Step 3
  const [templates, setTemplates] = useState(saved.templates || null);
  const [dayToDay, setDayToDay] = useState(saved.dayToDay || []);
  const [topics, setTopics] = useState(saved.topics || []);
  const [modality, setModality] = useState(saved.modality || "");
  const [strengths, setStrengths] = useState(saved.strengths || []);

  // Step 4
  const [conceptQs, setConceptQs] = useState(saved.conceptQs || []);
  const [conceptAns, setConceptAns] = useState(saved.conceptAns || []);
  const [confidence, setConfidence] = useState(saved.confidence ?? 3);

  // Persist on change
  useEffect(() => {
    saveState({
      step, pathType, role, country, remoteOnly, hybridOk, budgetIdx, budgetRange,
      timeHorizon, clarityBefore, whyNow, whyNowOther, priorities, constraints,
      learningStyle, templates, dayToDay, topics, modality, strengths,
      conceptQs, conceptAns, confidence
    });
  }, [step, pathType, role, country, remoteOnly, hybridOk, budgetIdx, budgetRange,
      timeHorizon, clarityBefore, whyNow, whyNowOther, priorities, constraints,
      learningStyle, templates, dayToDay, topics, modality, strengths,
      conceptQs, conceptAns, confidence]);

  // Fetch templates when entering step 2 (need pathType known)
  const fetchTemplates = useCallback(async () => {
    if (templates) return;
    try {
      const res = await axios.post(`${API}/templates`, { path_type: pathType, topics });
      setTemplates(res.data);
      if (res.data.concept_questions) {
        setConceptQs(res.data.concept_questions);
        setConceptAns(new Array(res.data.concept_questions.length).fill(null));
      }
    } catch { /* use defaults */ }
  }, [pathType, topics, templates]);

  // Track step events
  useEffect(() => {
    const sid = sessionStorage.getItem("synaptick_session_id");
    axios.post(`${API}/analytics/event`, {
      event_type: "step_started", session_id: sid, data: { step }
    }).catch(() => {});
  }, [step]);

  const toggleArr = (val, arr, setArr, max = null) => {
    if (arr.includes(val)) { setArr(arr.filter(v => v !== val)); }
    else if (max && arr.length >= max) { setArr([...arr.slice(1), val]); }
    else { setArr([...arr, val]); }
  };

  const handleNext = async () => {
    if (step === 0) {
      if (!pathType) { toast.error("Select a path type."); return; }
      if (!role) { toast.error("Select your role."); return; }
      await fetchTemplates();
    }
    if (step === 1) {
      if (whyNow.length < 1) { toast.error("Select at least one reason."); return; }
      if (priorities.length !== 2) { toast.error("Select exactly 2 priorities."); return; }
      // Fetch templates for step 3 if not loaded
      if (!templates) await fetchTemplates();
    }
    if (step === 2) {
      if (dayToDay.length !== 2) { toast.error("Select exactly 2 day-to-day items."); return; }
      // Fetch concept questions using selected topics
      try {
        const res = await axios.post(`${API}/templates`, { path_type: pathType, topics });
        setConceptQs(res.data.concept_questions || []);
        if (!conceptAns.length || conceptAns.length !== (res.data.concept_questions?.length || 0)) {
          setConceptAns(new Array((res.data.concept_questions || []).length).fill(null));
        }
      } catch { /* use whatever we have */ }
    }
    if (step === 3) {
      await submitSession();
      return;
    }
    setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
    else navigate("/");
  };

  const handleStartOver = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setStep(0); setPathType(""); setRole(""); setCountry("India");
    setRemoteOnly(false); setHybridOk(false); setBudgetIdx(1); setBudgetRange("");
    setTimeHorizon(""); setClarityBefore(3); setWhyNow([]); setWhyNowOther("");
    setPriorities([]); setConstraints([]); setLearningStyle("");
    setTemplates(null); setDayToDay([]); setTopics([]);
    setModality(""); setStrengths([]); setConceptQs([]); setConceptAns([]); setConfidence(3);
  };

  const submitSession = async () => {
    setLoading(true);
    try {
      const sid = sessionStorage.getItem("synaptick_session_id") || "unknown";
      const completionTime = Math.round((Date.now() - startTime) / 1000);
      const payload = {
        session_id: sid,
        user_inputs: {
          path_type: pathType, role, location: { country, remote_only: remoteOnly, hybrid_ok: hybridOk },
          budget_sensitivity: BUDGET_LABELS[budgetIdx], budget_range: budgetRange,
          time_horizon: timeHorizon, clarity_before: clarityBefore,
          why_now: whyNow.includes("Other") && whyNowOther ? [...whyNow.filter(w => w !== "Other"), whyNowOther] : whyNow,
          priorities, constraints, learning_style: learningStyle,
          day_to_day: dayToDay, topics, modality, strengths,
          concept_questions: conceptQs, concept_answers: conceptAns, confidence,
          partner: sessionStorage.getItem("synaptick_partner") || "educrib",
          vertical: "university_college", completion_time: completionTime
        },
        consent_to_store: false
      };
      const res = await axios.post(`${API}/session/complete`, payload);
      sessionStorage.setItem("synaptick_result", JSON.stringify(res.data));
      sessionStorage.setItem("synaptick_clarity_before", String(clarityBefore));

      // Track completion
      await axios.post(`${API}/analytics/event`, {
        event_type: "session_completed", session_id: sid,
        data: { completion_time: completionTime }
      }).catch(() => {});

      sessionStorage.removeItem(STORAGE_KEY);
      navigate("/results");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const progress = ((step + 1) / 4) * 100;
  const dtdOptions = templates?.day_to_day_options || STEP_INFO;
  const topicSets = templates?.topic_options || { "General": ["Business", "Finance", "Healthcare", "Arts", "Education", "Law"] };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display text-sm font-semibold text-foreground">Synaptick</span>
            <span data-testid="wizard-step-label" className="text-xs text-muted-foreground">Step {step + 1} of 4</span>
          </div>
          <Progress data-testid="wizard-progress" value={progress} className="h-1.5" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} variants={stepVariants} initial="initial" animate="animate" exit="exit">
            <div className="mb-6">
              <h2 data-testid="step-title" className="font-display text-xl sm:text-2xl font-semibold text-foreground">{STEP_INFO[step]?.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{STEP_INFO[step]?.sub}</p>
            </div>

            {/* ════════════════ STEP 1 ════════════════ */}
            {step === 0 && (
              <div className="space-y-7">
                {/* Path type cards */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-3">What are you looking for?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PATH_TYPES.map(pt => (
                      <button key={pt} data-testid={`path-${pt.slice(0,12).replace(/\s+/g,'-').toLowerCase()}`}
                        className={`text-left p-3 rounded-lg border text-sm transition-colors ${pathType === pt ? 'border-[hsl(var(--ring))] bg-accent text-accent-foreground' : 'border-border bg-card hover:bg-secondary/60'}`}
                        onClick={() => setPathType(pt)}>{pt}</button>
                    ))}
                  </div>
                </div>
                {/* Role */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Your role</label>
                  <div className="flex flex-wrap gap-2">
                    {ROLES.map(r => (
                      <button key={r} data-testid={`role-${r.slice(0,8).replace(/\s+/g,'-').toLowerCase()}`}
                        className={`chip-option ${role === r ? 'selected' : ''}`}
                        onClick={() => setRole(r)}>{r}</button>
                    ))}
                  </div>
                </div>
                {/* Location */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Country</label>
                  <div className="flex flex-wrap gap-2">
                    {COUNTRIES.map(c => (
                      <button key={c} className={`chip-option ${country === c ? 'selected' : ''}`}
                        onClick={() => setCountry(c)}>{c}</button>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-3">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                      <input type="checkbox" checked={remoteOnly} onChange={e => setRemoteOnly(e.target.checked)} className="rounded" /> Remote only
                    </label>
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                      <input type="checkbox" checked={hybridOk} onChange={e => setHybridOk(e.target.checked)} className="rounded" /> Hybrid acceptable
                    </label>
                  </div>
                </div>
                {/* Budget */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Budget sensitivity</label>
                  <div className="flex items-center gap-4 max-w-xs">
                    <Slider value={[budgetIdx]} onValueChange={v => setBudgetIdx(v[0])} min={0} max={2} step={1} className="flex-1" />
                    <span className="text-sm font-medium text-foreground w-16 text-right">{BUDGET_LABELS[budgetIdx]}</span>
                  </div>
                  {budgetIdx === 2 && (
                    <Input className="mt-2 max-w-xs" placeholder="Rough annual budget range (optional)" value={budgetRange} onChange={e => setBudgetRange(e.target.value)} />
                  )}
                </div>
                {/* Time horizon */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Time horizon</label>
                  <div className="flex flex-wrap gap-2">
                    {TIME_HORIZONS.map(t => (
                      <button key={t} className={`chip-option ${timeHorizon === t ? 'selected' : ''}`}
                        onClick={() => setTimeHorizon(t)}>{t}</button>
                    ))}
                  </div>
                </div>
                {/* Clarity */}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">How clear do you feel right now?</label>
                  <p className="text-xs text-muted-foreground mb-3">1 = totally stuck, 5 = very clear</p>
                  <div className="flex items-center gap-3">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} data-testid={`clarity-${n}`} className={`rating-dot ${clarityBefore === n ? 'active' : ''}`}
                        onClick={() => setClarityBefore(n)}>{n}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ STEP 2 ════════════════ */}
            {step === 1 && (
              <div className="space-y-7">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-3">Why now? (select at least 1)</label>
                  <div className="flex flex-wrap gap-2">
                    {WHY_NOW.map(w => (
                      <button key={w} data-testid={`why-${w.slice(0,12).replace(/\s+/g,'-').toLowerCase()}`}
                        className={`chip-option ${whyNow.includes(w) ? 'selected' : ''}`}
                        onClick={() => toggleArr(w, whyNow, setWhyNow)}>{w}</button>
                    ))}
                  </div>
                  {whyNow.includes("Other") && (
                    <Input className="mt-2 max-w-sm" placeholder="Tell us more..." value={whyNowOther} onChange={e => setWhyNowOther(e.target.value)} />
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">What matters most? (pick exactly 2)</label>
                  <div className="flex flex-wrap gap-2">
                    {WHAT_MATTERS.map(m => (
                      <button key={m} data-testid={`matter-${m.slice(0,8).replace(/\s+/g,'-').toLowerCase()}`}
                        className={`chip-option ${priorities.includes(m) ? 'selected' : ''}`}
                        onClick={() => toggleArr(m, priorities, setPriorities, 2)}>{m}</button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{priorities.length}/2 selected</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Constraints (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {CONSTRAINTS.map(c => (
                      <button key={c} className={`chip-option ${constraints.includes(c) ? 'selected' : ''}`}
                        onClick={() => toggleArr(c, constraints, setConstraints)}>{c}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Learning style (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {LEARNING_STYLES.map(l => (
                      <button key={l} className={`chip-option ${learningStyle === l ? 'selected' : ''}`}
                        onClick={() => setLearningStyle(l)}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ STEP 3 ════════════════ */}
            {step === 2 && (
              <div className="space-y-7">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-3">What do you think day-to-day looks like? (pick exactly 2)</label>
                  <div className="space-y-2">
                    {(templates?.day_to_day_options || ["Mostly lectures", "Mostly projects", "Mostly research", "Not sure"]).map((o, i) => (
                      <button key={i} data-testid={`dtd-${i}`}
                        className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${dayToDay.includes(o) ? 'border-[hsl(var(--ring))] bg-accent text-accent-foreground' : 'border-border bg-card hover:bg-secondary/60'}`}
                        onClick={() => toggleArr(o, dayToDay, setDayToDay, 2)}>{o}</button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{dayToDay.length}/2 selected</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-3">Topics you are most drawn to (up to 3)</label>
                  {Object.entries(topicSets).map(([cat, items]) => (
                    <div key={cat} className="mb-3">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{cat}</p>
                      <div className="flex flex-wrap gap-2">
                        {items.map(t => (
                          <button key={t} data-testid={`topic-${t.slice(0,8).replace(/[\s/]+/g,'-').toLowerCase()}`}
                            className={`chip-option ${topics.includes(t) ? 'selected' : ''}`}
                            onClick={() => toggleArr(t, topics, setTopics, 3)}>{t}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">{topics.length}/3 selected</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Preferred delivery mode</label>
                  <div className="flex flex-wrap gap-2">
                    {MODALITIES.map(m => (
                      <button key={m} className={`chip-option ${modality === m ? 'selected' : ''}`}
                        onClick={() => setModality(m)}>{m}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Your strengths (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {STRENGTHS.map(s => (
                      <button key={s} className={`chip-option ${strengths.includes(s) ? 'selected' : ''}`}
                        onClick={() => toggleArr(s, strengths, setStrengths)}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ STEP 4 ════════════════ */}
            {step === 3 && (
              <div className="space-y-7">
                {conceptQs.map((q, qi) => (
                  <Card key={qi} className="overflow-hidden">
                    <CardContent className="pt-5">
                      <p className="text-sm font-medium text-foreground mb-3">{qi + 1}. {q.q}</p>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => (
                          <button key={oi} data-testid={`q${qi}-opt${oi}`}
                            className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${conceptAns[qi] === oi ? 'border-[hsl(var(--ring))] bg-accent text-accent-foreground' : 'border-border bg-white hover:bg-secondary/60'}`}
                            onClick={() => { const a = [...conceptAns]; a[qi] = oi; setConceptAns(a); }}>{opt}</button>
                        ))}
                        <button data-testid={`q${qi}-idk`}
                          className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${conceptAns[qi] === -1 ? 'border-[hsl(var(--ring))] bg-accent text-accent-foreground' : 'border-border bg-white hover:bg-secondary/60'}`}
                          onClick={() => { const a = [...conceptAns]; a[qi] = -1; setConceptAns(a); }}>Not sure</button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">How confident are you in those answers?</label>
                  <p className="text-xs text-muted-foreground mb-3">1 = not at all, 5 = very confident</p>
                  <div className="flex items-center gap-3">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} data-testid={`confidence-${n}`} className={`rating-dot ${confidence === n ? 'active' : ''}`}
                        onClick={() => setConfidence(n)}>{n}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom bar */}
      <div className="sticky bottom-0 z-30 bg-background/85 backdrop-blur border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <Button data-testid="wizard-back" variant="secondary" onClick={handleBack} className="rounded-[12px] gap-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <Button data-testid="wizard-next" onClick={handleNext} disabled={loading} className="rounded-[12px] gap-2">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Generating brief...</>) :
               step === 3 ? (<>Get Clarity Brief<ArrowRight className="w-4 h-4" /></>) :
               (<>Continue<ArrowRight className="w-4 h-4" /></>)}
            </Button>
          </div>
          <div className="flex justify-center mt-2">
            <button data-testid="wizard-start-over" onClick={handleStartOver}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw className="w-3 h-3" /> Start over
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
